import { FormArray } from '@angular/forms';
import { PDFDocument } from 'pdf-lib';
import { HealthQuestionsModel } from '@shared/extensions/user-data-extensions';
import { LabelTextPipe } from '@shared/pipes/label-text.pipe';
import { Regle } from '@webapi/MIF.Subscription.Parrot';
import { Profile } from '@webapi/MIF.Subscription.WebApi';
import { WATERMARK_BROUILLON_PNG_BASE64 } from './watermark-image.const';

async function addWatermarkToPdfBase64(pdfBase64: string): Promise<string> {
  const pdfDoc = await PDFDocument.load(base64ToUint8Array(pdfBase64));
  const watermarkImage = await pdfDoc.embedPng(
    base64ToUint8Array(
      WATERMARK_BROUILLON_PNG_BASE64.replace('data:image/png;base64,', '')
    )
  );

  for (const page of pdfDoc.getPages()) {
    const { width, height } = page.getSize();
    const targetWidth = width * 0.72;
    const ratio = targetWidth / watermarkImage.width;
    const imageHeight = watermarkImage.height * ratio;
    const verticalStep = Math.max(imageHeight * 0.9, 180);
    const minPadding = 24;
    const maxY = Math.max(minPadding, height - imageHeight - minPadding);

    for (let y = maxY; y >= minPadding; y -= verticalStep) {
      page.drawImage(watermarkImage, {
        x: (width - targetWidth) / 2,
        y,
        width: targetWidth,
        height: imageHeight,
        opacity: 0.16
      });
    }

    if (maxY === minPadding) {
      page.drawImage(watermarkImage, {
        x: (width - targetWidth) / 2,
        y: minPadding,
        width: targetWidth,
        height: imageHeight,
        opacity: 0.16
      });
    }
  }

  return uint8ArrayToBase64(await pdfDoc.save());
}

function base64ToUint8Array(base64: string): Uint8Array {
  const normalized = base64.replace(/\s/g, '');
  const binary = atob(normalized);
  const bytes = new Uint8Array(binary.length);

  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }

  return bytes;
}

function uint8ArrayToBase64(bytes: Uint8Array): string {
  let binary = '';

  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }

  return btoa(binary);
}

function generateTableData(
  fundInEuroRegle: Regle,
  initialPaymentWithoutPercents: number
): TableItem[] {
  const startYear = new Date().getFullYear();
  const data: TableItem[] = [];
  const fundPercent = parseFloat(fundInEuroRegle.valeur.replace(',', '.'));

  for (let i = 0; i < 8; i++) {
    data.push({
      year: startYear + i,
      date: `31/12/${startYear + i}`,
      sumPaid: initialPaymentWithoutPercents,
      repurchaseValue: calculateRepurchaseValue(
        data,
        i,
        fundPercent,
        initialPaymentWithoutPercents
      )
    } as TableItem);
  }

  return data;
}

function calculateRepurchaseValue(
  data: TableItem[],
  i: number,
  fundPercent: number,
  initialPaymentWithoutPercents: number
): number {
  if (i > 0) {
    return (
      data[i - 1].repurchaseValue -
      (data[i - 1].repurchaseValue / 100) * fundPercent
    );
  }

  return (
    initialPaymentWithoutPercents -
    (initialPaymentWithoutPercents / 100) * fundPercent
  );
}

function getHowDidYouHearMifText(
  model: AdvertisementSourceDataModel,
  labelTextPipe: LabelTextPipe
): string {
  if (model.advertisementSourceCode) {
    if (model.advertisementSourceCode === Sources.GALITO) {
      // todo: remove fallback when SUMMARY.GalitoReferralNotice label is deployed
      const notice =
        labelTextPipe.transform('SUMMARY.GalitoReferralNotice') ||
        'Vous avez connu la MIF par l’indication du partenaire Galito (7200)';
      return `${notice} `;
    }
    if (
      model.advertisementSourceCode === Sources.PARRAINAGE &&
      model.sponsorNumber
    ) {
      return `${labelTextPipe.transform('SUMMARY.HowDidYouHearMifText1')} <span style="color: #189bd5" class="answer">${labelTextPipe.transform(
        'SUMMARY.HowDidYouHearMifText2'
      )}</span> ${labelTextPipe.transform('SUMMARY.HowDidYouHearMifText3')} <span style="color: #189bd5" class="answer"> ${model.sponsorNumber}</span>. `;
    }
    if (model.advertisementSourceCode === Sources.MEMBRE_CORRESPONDANT) {
      const memberName = model.firstName && model.lastName ? `${model.firstName} ${model.lastName}` : '';
      const memberDetails = [model.memberNumber, memberName].filter(Boolean).join(' et ');
      if (!memberDetails) {
        return '';
      }

      const member = document.createElement('span');
      member.className = 'answer';
      member.style.color = '#189bd5';
      member.textContent = memberDetails;
      // todo: remove fallback when SUMMARY.CorrespondingMemberReferralNotice label is deployed
      const notice =
        labelTextPipe.transform('SUMMARY.CorrespondingMemberReferralNotice') ||
        'Vous avez connu la MIF par l’indication du membre correspondant';
      const referralNotice = `${notice} ${member.outerHTML}. `;

      if (model.memberNumber && model.firstName && model.lastName) {
        return `${referralNotice}${labelTextPipe.transform('SUMMARY.HowDidYouHearMifText1')} <span style="color: #189bd5" class="answer">${labelTextPipe.transform(
          'SUMMARY.HowDidYouHearMifText4'
        )}</span> ${labelTextPipe.transform('SUMMARY.HowDidYouHearMifText5')} <span style="color: #189bd5" class="answer">${model.memberNumber}</span> et <span style="color: #189bd5" class="answer">${
          model.firstName
        } ${model.lastName}</span>. `;
      } else if (model.firstName && model.lastName) {
        // only first and lastname
        return `${referralNotice}${labelTextPipe.transform('SUMMARY.HowDidYouHearMifText1')} <span style="color: #189bd5" class="answer">${labelTextPipe.transform(
          'SUMMARY.HowDidYouHearMifText4'
        )}</span> ${labelTextPipe.transform('SUMMARY.HowDidYouHearMifText6')} <span style="color: #189bd5" class="answer">${model.firstName} ${model.lastName}</span>. `;
      }
      // only code
      return `${referralNotice}${labelTextPipe.transform('SUMMARY.HowDidYouHearMifText1')} <span style="color: #189bd5" class="answer">${labelTextPipe.transform(
        'SUMMARY.HowDidYouHearMifText4'
      )}</span> ${labelTextPipe.transform('SUMMARY.HowDidYouHearMifText7')} <span style="color: #189bd5" class="answer">${model.memberNumber}</span>. `;
    }
    if (model.advertisementSourceCode === Sources.INTERNET) {
      return `${labelTextPipe.transform('SUMMARY.HowDidYouHearMifText1')} <span style="color: #189bd5" class="answer">${labelTextPipe.transform('SUMMARY.HowDidYouHearMifText8')}</span>. `;
    }
    if (model.advertisementSourceCode === Sources.RESEAUX_SOCIAUX) {
      return `${labelTextPipe.transform('SUMMARY.HowDidYouHearMifText1')} <span style="color: #189bd5" class="answer">${labelTextPipe.transform('SUMMARY.HowDidYouHearMifText9')}</span>. `;
    }
    if (model.advertisementSourceCode === Sources.BOUCHE_A_OREILLE) {
      return `${labelTextPipe.transform('SUMMARY.HowDidYouHearMifText10')} <span style="color: #189bd5" class="answer">${labelTextPipe.transform('SUMMARY.HowDidYouHearMifText11')}</span>. `;
    }
    if (model.advertisementSourceCode === Sources.PRESSE) {
      return `${labelTextPipe.transform('SUMMARY.HowDidYouHearMifText10')} <span style="color: #189bd5" class="answer">${labelTextPipe.transform('SUMMARY.HowDidYouHearMifText12')}</span>. `;
    }
    if (model.advertisementSourceCode === Sources.AUTRE) {
      return `${labelTextPipe.transform('SUMMARY.HowDidYouHearMifText10')} <span style="color: #189bd5" class="answer">${labelTextPipe.transform('SUMMARY.HowDidYouHearMifText13')}</span>. `;
    }
  }
  return '';
}

function isShowSEPA(clientProfile: Profile) {
  return true;
  // let isShow = true;

  // if (!!clientProfile.transferContractCode && !clientProfile.initialPaymentAmount && !clientProfile.scheduledPaymentAmount) {
  //   isShow = false;
  // }

  // return isShow;
}

export {
  addWatermarkToPdfBase64,
  generateTableData,
  getHowDidYouHearMifText,
  isShowSEPA
};

export class TableItem {
  year: number;
  date: string;
  sumPaid: number;
  repurchaseValue: number;
}

export class FileLink {
  uniqueId: string;
  name: string;
}

export enum Sources {
  INTERNET = 'INTERNET',
  PARRAINAGE = 'PARRAINAGE',
  RESEAUX_SOCIAUX = 'RESEAUX_SOCIAUX',
  BOUCHE_A_OREILLE = 'BOUCHE_A_OREILLE',
  PRESSE = 'PRESSE',
  PARTENAIRE = 'PARTENAIRE',
  GALITO = 'GALITO',
  MEMBRE_CORRESPONDANT = 'MEMBRE_CORRESPONDANT',
  AUTRE = 'AUTRE'
}

export interface GpaSubscriptionModel {
  health: HealthQuestionsModel;
  reuseBeneficiaries: boolean;
  beneficiaryClauseCode?: number;
  beneficiaryFreeClauseText?: string;
  beneficiaries?: FormArray;
  isMultipliedCoverage?: boolean;
  isEqualParts?: boolean;
}

export interface AdvertisementSourceDataModel {
  advertisementSourceCode?: string;
  sponsorNumber?: string;
  memberNumber?: string;
  firstName?: string;
  lastName?: string;
  promoCode?: string;
  partnerCode?: string;
  mifAdvisorCode?: string;
  utm?: string;
}
